/**
 * 腾讯云即时通讯IM系统通知模块
 */
import { getTIMInstance } from './core';
import TencentCloudChat from '@tencentcloud/chat';

// 使用SDK提供的系统通知类型枚举
export const SYSTEM_NOTIFICATION_TYPE = {
  FRIEND_REQUEST: TencentCloudChat.TYPES.MSG_FRIEND_SYSTEM_NOTICE,
  GROUP_SYSTEM_NOTICE: TencentCloudChat.TYPES.MSG_GRP_SYS_NOTICE,
  GROUP_TIP: TencentCloudChat.TYPES.MSG_GRP_TIP,
  SYSTEM_MESSAGE: 'system_message', // 通用系统消息
};

/**
 * 验证用户是否可以执行系统通知操作的函数
 * 通过尝试获取系统通知列表来验证登录状态
 * @returns {Promise<boolean>} 是否可以执行操作
 */
async function verifyUserCanPerformOperations() {
  const tim = getTIMInstance();
  if (!tim) {
    return false;
  }

  try {
    // 尝试获取好友申请列表来验证登录状态
    await tim.getFriendApplicationList();
    console.log('用户登录状态验证成功，可以执行系统通知操作');
    return true;
  } catch (error) {
    console.warn('用户登录状态验证失败:', error.message);
    // 检查错误码是否表明用户未登录
    if (error.code === 6014 || error.code === 6015) {
      return false;
    }
    // 其他错误可能是网络或权限问题，不一定表示未登录
    return true;
  }
}

/**
 * 检查TIM实例状态的调试函数
 * @returns {Object} 状态检查结果
 */
function checkTIMInstanceStatus() {
  const tim = getTIMInstance();
  const status = {
    hasInstance: !!tim,
    isLoggedIn: false,
    currentUser: null,
    hasMethods: {
      getMyProfile: false,
      handleGroupApplication: false,
      acceptFriendApplication: false,
      refuseFriendApplication: false
    },
    error: null
  };

  if (!tim) {
    status.error = 'TIM实例不存在';
    return status;
  }

  try {
    // 检查基本方法
    status.hasMethods.getMyProfile = typeof tim.getMyProfile === 'function';
    status.hasMethods.handleGroupApplication = typeof tim.handleGroupApplication === 'function';
    status.hasMethods.acceptFriendApplication = typeof tim.acceptFriendApplication === 'function';
    status.hasMethods.refuseFriendApplication = typeof tim.refuseFriendApplication === 'function';

    // 检查登录状态 - 采用多种方法验证
    let loginCheckMethods = [];

    // 方法1: 尝试getMyProfile
    try {
      const profile = tim.getMyProfile();
      if (profile?.userID) {
        status.isLoggedIn = true;
        status.currentUser = profile.userID;
        loginCheckMethods.push('getMyProfile');
        console.log('getMyProfile检查成功，用户已登录:', profile.userID);
      }
    } catch (profileError) {
      console.warn('getMyProfile失败，尝试其他方法检查登录状态:', profileError.message);
      loginCheckMethods.push('getMyProfile_failed');
    }

    // 方法2: 检查TIM实例是否有基本功能可用（如果有handleGroupApplication方法且实例存在，通常表示已登录）
    if (!status.isLoggedIn && status.hasMethods.handleGroupApplication) {
      // 如果SDK实例完整且功能可用，可能已经登录了
      // 我们可以假设用户已登录，让实际的API调用来验证
      status.isLoggedIn = true;
      status.currentUser = 'unknown'; // 用户ID未知但可能已登录
      loginCheckMethods.push('method_available_check');
      console.log('基于方法可用性推断用户可能已登录');
    }

    console.log('登录状态检查方法:', loginCheckMethods);

  } catch (error) {
    status.error = `检查TIM实例失败: ${error.message}`;
  }

  return status;
}

/**
 * 获取系统通知列表
 * @param {Object} options 选项
 * @param {number} options.count 获取数量，默认20
 * @returns {Promise<Array>} 系统通知列表
 */
export async function getSystemNotifications(options = {}) {
  const { count = 20 } = options;

  const tim = getTIMInstance();
  if (!tim) {
    console.error('TIM SDK未初始化，无法获取系统通知');
    return [];
  }

  try {
    console.log('开始获取系统通知...');
    
    // 获取好友申请列表
    const friendApplications = await getFriendApplicationList();
    
    // 获取群申请列表
    const groupApplications = await getGroupApplicationList();
    
    // 合并并格式化通知
    const notifications = [];
    
    // 处理好友申请
    friendApplications.forEach(application => {
      notifications.push(processFriendApplication(application));
    });
    
    // 处理群申请
    groupApplications.forEach(application => {
      notifications.push(processGroupApplication(application));
    });
    
    // 按时间排序（最新的在前）
    notifications.sort((a, b) => new Date(b.time) - new Date(a.time));
    
    // 限制数量
    const limitedNotifications = notifications.slice(0, count);
    
    console.log(`获取到 ${limitedNotifications.length} 条系统通知`);
    return limitedNotifications;
  } catch (error) {
    console.error('获取系统通知失败', error);
    return [];
  }
}

/**
 * 获取好友申请列表
 * @returns {Promise<Array>} 好友申请列表
 */
async function getFriendApplicationList() {
  const tim = getTIMInstance();
  if (!tim) return [];

  try {
    const imResponse = await tim.getFriendApplicationList();
    return imResponse.data.applicationList || [];
  } catch (error) {
    console.error('获取好友申请列表失败', error);
    return [];
  }
}

/**
 * 获取群申请列表
 * @returns {Promise<Array>} 群申请列表
 */
async function getGroupApplicationList() {
  const tim = getTIMInstance();
  if (!tim) return [];

  try {
    const imResponse = await tim.getGroupApplicationList();
    return imResponse.data.applicationList || [];
  } catch (error) {
    console.error('获取群申请列表失败', error);
    return [];
  }
}

/**
 * 处理好友申请
 * @param {Object} application 好友申请对象
 * @returns {Object} 处理后的通知对象
 */
function processFriendApplication(application) {
  return {
    id: `friend_${application.userID}_${application.time}`,
    type: TencentCloudChat.TYPES.MSG_FRIEND_SYSTEM_NOTICE,
    title: '好友请求',
    content: `${application.nick || application.userID} 请求添加您为好友`,
    time: new Date(application.time * 1000),
    fromUserID: application.userID,
    remark: application.wording || '',
    actionRequired: application.type === 1, // 1表示收到的申请
    raw: application,
  };
}

/**
 * 处理群申请
 * @param {Object} application 群申请对象
 * @returns {Object} 处理后的通知对象
 */
function processGroupApplication(application) {
  // 检查申请类型：0表示收到的申请，需要处理；其他表示已处理或发出的申请
  const needsAction = application.applicationType === 0 || application.type === 0;
  
  return {
    id: `group_${application.groupID}_${application.applicant}_${application.time || Date.now()}`,
    type: TencentCloudChat.TYPES.MSG_GRP_SYS_NOTICE,
    title: '群系统通知',
    content: `${application.applicantNick || application.applicant} 申请加入群聊"${application.groupName || application.groupID}"`,
    time: application.time ? new Date(application.time * 1000) : new Date(),
    groupID: application.groupID,
    groupName: application.groupName,
    operatorID: 'system',
    targetUserIDs: [application.applicant],
    applicantID: application.applicant, // 添加申请人ID字段
    actionRequired: needsAction,
    raw: application,
  };
}



/**
 * 处理好友请求
 * @param {string} userID 用户ID
 * @param {string} action 操作: 'accept' | 'reject'
 * @param {string} remark 备注
 * @param {boolean} useMock 是否使用Mock，默认false
 * @returns {Promise<Object>} 处理结果
 */
export async function handleFriendRequest(userID, action, remark = '') {
  const tim = getTIMInstance();
  if (!tim) {
    throw new Error('TIM SDK未初始化');
  }

  try {
    if (action === 'accept') {
      // 使用SDK接受好友申请方法
      const imResponse = await tim.acceptFriendApplication({
        userID,
        remark
      });
      return imResponse.data;
    } else if (action === 'reject') {
      // 使用SDK拒绝好友申请方法
      const imResponse = await tim.refuseFriendApplication({
        userID
      });
      return imResponse.data;
    } else {
      throw new Error(`不支持的操作: ${action}`);
    }
  } catch (error) {
    console.error('处理好友请求失败', error);
    throw error;
  }
}

/**
 * 处理群邀请/加群申请
 * @param {string} groupID 群ID
 * @param {string} action 操作: 'accept' | 'reject'
 * @param {string} applicantID 申请人ID（用于处理加群申请）
 * @param {Object} options 额外选项，如authentication等
 * @returns {Promise<Object>} 处理结果
 */
export async function handleGroupRequest(groupID, action, applicantID = '', options = {}) {
  // 首先进行TIM实例状态检查
  const timStatus = checkTIMInstanceStatus();
  console.log('TIM实例状态检查结果:', timStatus);
  
  if (!timStatus.hasInstance) {
    throw new Error('TIM SDK未初始化');
  }
  
  // 只有在明确确定错误时才抛出异常，对于登录状态不确定的情况让API调用来验证
  if (timStatus.error && timStatus.error.includes('用户确实未登录')) {
    throw new Error(`TIM状态异常: ${timStatus.error}`);
  }
  
  if (!timStatus.hasMethods.handleGroupApplication) {
    throw new Error('TIM实例缺少群申请处理方法');
  }

  // 验证用户是否真正可以执行操作
  const canPerformOps = await verifyUserCanPerformOperations();
  if (!canPerformOps) {
    throw new Error('用户未登录或无权限执行此操作，请重新登录');
  }

  const tim = getTIMInstance();
  
  // 验证参数
  if (!groupID || typeof groupID !== 'string') {
    throw new Error(`无效的群ID: ${groupID}`);
  }
  
  console.log('接收到的群ID参数:', {
    原始群ID: groupID,
    群ID类型: typeof groupID,
    群ID长度: groupID.length
  });
  
  if (!action || !['accept', 'reject'].includes(action)) {
    throw new Error(`无效的操作类型: ${action}`);
  }

  try {
    console.log(`处理群请求开始:`, {
      groupID: groupID,
      action: action,
      applicantID: applicantID,
      options: options,
      timInstance: !!tim,
      timReady: tim ? !!tim.getMyProfile : false
    });
    
    // 重要：Web SDK的handleGroupApplication需要传递完整的群申请对象
    // 我们需要先获取群申请列表，找到对应的申请对象
    let groupApplication = null;
    
    // 如果options中包含原始申请数据，直接使用
    if (options.rawApplication) {
      groupApplication = options.rawApplication;
      console.log('使用传入的原始申请数据:', groupApplication);
      
      // 验证原始申请数据的必要字段
      if (!groupApplication.groupID) {
        console.error('原始申请数据缺少groupID字段:', groupApplication);
        throw new Error('申请数据不完整，缺少群ID');
      }
    } else {
      // 否则从群申请列表中查找
      console.log('从群申请列表中查找对应的申请...');
      const imResponse = await tim.getGroupApplicationList();
      const applicationList = imResponse.data.applicationList || [];
      
      console.log('群申请列表:', applicationList);
      
      // 查找匹配的群申请 - 支持多种ID格式匹配
      groupApplication = applicationList.find(app => {
        // 检查群ID匹配（支持不同格式）
        const groupIDMatch = app.groupID === groupID || 
                            app.groupID === groupID.replace(/^GROUP/, '') ||
                            `GROUP${app.groupID}` === groupID;
        
        // 检查申请人匹配
        const applicantMatch = app.applicant === applicantID || 
                              app.fromUser === applicantID ||
                              app.userID === applicantID;
        
        console.log('匹配检查:', {
          申请记录: app,
          群ID匹配: groupIDMatch,
          申请人匹配: applicantMatch,
          目标群ID: groupID,
          目标申请人: applicantID
        });
        
        return groupIDMatch && applicantMatch;
      });
      
      if (!groupApplication) {
        console.error('未找到匹配的群申请:', {
          目标群ID: groupID,
          目标申请人: applicantID,
          可用申请列表: applicationList.map(app => ({
            groupID: app.groupID,
            applicant: app.applicant || app.fromUser || app.userID
          }))
        });
        throw new Error(`未找到群ID ${groupID} 的申请记录，申请人: ${applicantID}`);
      }
      
      console.log('找到对应的群申请:', groupApplication);
    }
    
    // 根据腾讯云IM SDK文档，handleGroupApplication方法需要特定格式的参数
    console.log('准备调用handleGroupApplication，原始申请数据:', groupApplication);
    
    // 验证关键字段
    if (!groupApplication.groupID) {
      throw new Error('群申请对象缺少groupID字段');
    }
    if (!groupApplication.applicant) {
      throw new Error('群申请对象缺少applicant字段');
    }
    
    // 根据SDK文档，正确的参数格式应该是单个对象，包含handleAction和application
    const requestParams = {
      handleAction: action === 'accept' ? 'Agree' : 'Reject', // 使用字符串而不是常量
      application: groupApplication, // 完整的申请对象
      handleMessage: action === 'accept' ? '欢迎加入群聊' : '很抱歉，暂时无法通过您的申请'
    };

    console.log('调用tim.handleGroupApplication，参数详情:', {
      参数对象: requestParams,
      验证字段: {
        handleAction: requestParams.handleAction,
        'application.groupID': requestParams.application.groupID,
        'application.applicant': requestParams.application.applicant,
        'application.authentication': requestParams.application.authentication,
        'application.messageKey': requestParams.application.messageKey
      }
    });
    
    // 使用正确的单参数格式调用SDK方法
    const imResponse = await tim.handleGroupApplication(requestParams);
    console.log(`${action === 'accept' ? '同意' : '拒绝'}群申请成功:`, imResponse);
    return imResponse.data;

  } catch (error) {
    console.error('处理群请求失败:', error);
    throw error;
  }
}

/**
 * 标记系统通知为已读（系统申请类通知无需标记已读）
 * @returns {Promise<Object>} 结果
 */
export async function markSystemNotificationsRead() {
  const tim = getTIMInstance();
  if (!tim) {
    throw new Error('TIM SDK未初始化');
  }

  try {
    console.log('系统申请类通知无需标记已读，操作完成');
    return { status: 'success', message: '系统申请类通知无需标记已读' };
  } catch (error) {
    console.error('标记系统通知已读失败', error);
    throw error;
  }
}

/**
 * 处理外部传入的群申请通知数据
 * @param {Object} notificationData 通知数据对象
 * @returns {Object} 标准化的通知对象
 */
export function processExternalGroupNotification(notificationData) {
  // 确保actionRequired字段正确设置
  const actionRequired = notificationData.raw?.applicationType === 0 || 
                        notificationData.actionRequired === true;
  
  return {
    ...notificationData,
    actionRequired,
    // 确保申请人ID字段存在
    applicantID: notificationData.raw?.applicant || 
                 notificationData.applicantID || 
                 notificationData.targetUserIDs?.[0],
    // 标准化类型字段
    type: notificationData.type === 'TIMGroupSystemNoticeElem' ? 
          TencentCloudChat.TYPES.MSG_GRP_SYS_NOTICE : 
          notificationData.type
  };
} 